import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

const BookieSubscriptionSuccess: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateSubscriptionStatus } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verify = async () => {
      const sessionId = searchParams.get('session_id');
      if (!sessionId) {
        setVerifying(false);
        return;
      }

      // Give webhook time to process
      await new Promise(resolve => setTimeout(resolve, 2000));

      try {
        const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
        const response = await fetch(`${apiUrl}/bookie-subscription/status/${user?.id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await response.json();

        if (data.success && data.isSubscriber) {
          updateSubscriptionStatus(true, data.expirationDate);
          setSuccess(true);
        }
      } catch (err) {
        console.error('Failed to verify bookie subscription:', err);
      } finally {
        setVerifying(false);
      }
    };

    verify();
  }, [searchParams, user]);

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-yellow-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">{t('bookie.verifyingPayment')}</h2>
          <p className="text-emerald-200">{t('bookie.pleaseWaitConfirm')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-emerald-800 to-teal-800 rounded-xl p-8 border border-emerald-400 max-w-md w-full">
        <div className="text-center">
          {success ? (
            <>
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-3xl font-bold text-white mb-4">{t('bookie.success.title')}</h2>
              <div className="bg-white/10 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center space-x-2 mb-3">
                  
                  <span className="text-2xl font-bold text-yellow-300">{t('bookie.success.premiumMember')}</span>
                </div>
                <p className="text-emerald-200 text-sm">{t('bookie.success.active')}</p>
              </div>
              <div className="text-left text-emerald-200 mb-6 space-y-2">
                <p className="font-semibold text-white mb-2">{t('player.nowHaveAccess')}</p>
                <div className="flex items-start">
                  <span className="mr-2">✅</span>
                  <span>{t('bookie.success.benefit1')}</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-2">✅</span>
                  <span>{t('bookie.success.benefit2')}</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-2">✅</span>
                  <span>{t('bookie.success.benefit3')}</span>
                </div>
              </div>
              <button
                onClick={() => navigate('/bookie/dashboard')}
                className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-yellow-900 font-bold py-3 px-6 rounded-lg transition duration-200 transform hover:scale-105"
              >
                {t('bookie.success.continue')}
              </button>
            </>
          ) : (
            <>
              <div className="text-6xl mb-4">⚠️</div>
              <h2 className="text-3xl font-bold text-white mb-4">{t('bookie.success.paymentProcessing')}</h2>
              <p className="text-emerald-200 mb-6">{t('bookie.success.processingMessage')}</p>
              <button
                onClick={() => navigate('/bookie/dashboard')}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
              >
                {t('bookie.success.returnToDashboard')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookieSubscriptionSuccess;
